import { useState, useMemo } from "react";
import { BottomArrow } from "@/Icon/BottomArrow";
import { useBusinessSettlementInfo } from "~/services/business/myPage";

interface SettlementTicketItem {
  ticketName: string;
  amount: number;
}

const Amount = () => {
  // 현재 날짜 가져오기
  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear().toString();
    const month = `${now.getMonth() + 1}월`;
    return { year, month };
  };

  const currentDate = getCurrentDate();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentDate.year);
  const [selectedMonth, setSelectedMonth] = useState(currentDate.month);

  // selectedYear와 selectedMonth를 YYYYMM 형식으로 변환
  const yearMonth = useMemo(() => {
    const monthNumber = selectedMonth.replace("월", "").padStart(2, "0");
    return `${selectedYear}${monthNumber}`;
  }, [selectedYear, selectedMonth]);

  // API 호출
  const {
    data: settlementData,
    isLoading,
    error,
  } = useBusinessSettlementInfo({
    yearMonth: yearMonth,
  });

  // 지난달 버튼 클릭 핸들러
  const handleRegionChange = () => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const year = lastMonth.getFullYear().toString();
    const month = `${lastMonth.getMonth() + 1}월`;
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  // 오늘 버튼 클릭 핸들러
  const handleDateChange = () => {
    const today = getCurrentDate();
    setSelectedYear(today.year);
    setSelectedMonth(today.month);
  };

  const yearList = ["2024", "2023", "2022", "2021", "2020"];
  const monthList = [
    "1월",
    "2월",
    "3월",
    "4월",
    "5월",
    "6월",
    "7월",
    "8월",
    "9월",
    "10월",
    "11월",
    "12월",
  ];

  // tickets 데이터를 이용해 매출 목록 구성 (0원 제외)
  const serviceIncomeData: SettlementTicketItem[] = useMemo(() => {
    if (!settlementData?.data?.tickets) return [];
    return settlementData.data.tickets.filter((item) => item.amount > 0);
  }, [settlementData]);

  const totalAmount = useMemo(() => {
    return settlementData?.data?.totalSalesAmount ?? 0;
  }, [settlementData]);

  const formatNumber = (num: number) => {
    return num.toLocaleString("ko-KR");
  };

  const truncateText = (text: string, maxLen = 18) =>
    text.length <= maxLen ? text : `${text.slice(0, maxLen - 1)}...`;

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4 bg-gray-50">
        <div className="text-center">
          <div className="text-lg font-medium text-gray-600">
            데이터를 불러오는 중...
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 처리
  if (error) {
    return (
      <div className="flex justify-center items-center p-4 bg-gray-50">
        <div className="text-center">
          <div className="text-lg font-medium text-red-600">
            데이터를 불러오는데 실패했습니다.
          </div>
          <div className="mt-2 text-sm text-gray-500">
            잠시 후 다시 시도해주세요.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="p-4 bg-gray-50">
      <div className="mx-auto max-w-md rounded-lg">
        {/* 날짜 헤더 */}
        <div className="flex gap-2 justify-between items-center text-center bg-gray-100 rounded-t-lg">
          <div className="flex gap-2 items-center">
            <div className="relative">
              <div
                className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 w-[100px]"
                onClick={() => setDropdownOpen((v) => !v)}
              >
                <span className="mx-2 text-sm font-medium w-[45px]">
                  {selectedYear}
                </span>
                <BottomArrow />
              </div>
              {/* 드롭다운 메뉴 */}
              {dropdownOpen && (
                <div className="absolute z-10 bg-white border rounded shadow top-full mt-1 left-0 w-[100px] max-h-[180px] overflow-y-auto">
                  {yearList?.map((a: string) => (
                    <div
                      key={a}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-orange-50
                    ${a === selectedYear ? "text-orange-500 font-bold" : ""}`}
                      onClick={() => {
                        setSelectedYear(a);
                        setDropdownOpen(false);
                      }}
                    >
                      {a}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <div
                className="flex items-center gap-2 bg-white border border-gray-300 
        rounded-lg px-3 py-2 text-sm font-medium text-gray-700 w-[80px]"
                onClick={() => setMonthDropdownOpen((v) => !v)}
              >
                <span className="mx-2 text-sm font-medium w-[45px]">
                  {selectedMonth}
                </span>
                <BottomArrow />
              </div>
              {monthDropdownOpen && (
                <div className="absolute z-10 bg-white border rounded shadow top-full mt-1 left-0 w-[80px] max-h-[180px] overflow-y-auto">
                  {monthList?.map((month: string) => (
                    <div
                      key={month}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-orange-50
                  ${month === selectedMonth ? "text-orange-500 font-bold" : ""}`}
                      onClick={() => {
                        setSelectedMonth(month);
                        setMonthDropdownOpen(false);
                      }}
                    >
                      {month}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={handleRegionChange}
              className="flex gap-2 items-center px-3 py-2 ml-3 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300"
            >
              지난달
            </button>
            <button
              onClick={handleDateChange}
              className="flex gap-2 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              오늘
            </button>
          </div>
        </div>

        {/* 이번 달 수입 */}
        <div className="p-4 border-[1px] border-[#E9E9E9] bg-white rounded-lg">
          <h3 className="mb-4 font-semibold text-gray-800">매출 총액</h3>

          {/* 서비스별 수입 목록 */}
          <div className="mb-4 space-y-3">
            <div className="flex items-center justify-between border-b border-[#E9E9E9] pb-2 text-[#666666]">
              <div className="flex items-center">
                <span className="text-sm">서비스 유형</span>
              </div>
              <div className="flex items-center">
                <span className="text-sm">총 결제금액</span>
              </div>
            </div>
            {serviceIncomeData.map((item, index) => (
              <div key={index} className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-700">
                    {truncateText(item.ticketName)}
                  </span>
                </div>
                <div className="flex items-center">
                  {/* <span className="mr-4 text-sm text-gray-500">{item.percentage}%</span> */}
                  <span className="text-sm font-medium text-gray-800">
                    {formatNumber(item.amount)} 원
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* 합계 */}
          <div className="pt-3 border-t border-black">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-800">합계</span>
              <span className="text-lg font-bold text-gray-800">
                {formatNumber(totalAmount)} 원
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 border-[1px] border-[#E9E9E9] bg-white rounded-lg mt-4">
          <h3 className="mb-4 font-semibold text-gray-800">수수료 총액</h3>
          {/* 서비스별 수입 목록 */}
          <div className="mb-4 space-y-3">
            <div className="flex items-center justify-between border-b border-[#E9E9E9] pb-2 text-[#666666]">
              <div className="flex items-center">
                <span className="text-sm">결제 유형</span>
              </div>
              <div className="flex items-center">
                <span className="text-sm">총 수수료</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700">
                  플랫폼 수수료
                </span>
                <span className="text-sm text-[#666666] ">
                  (PG수수료 포함, {settlementData?.data?.totalFeeRate ?? 0}%)
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-800">
                  {formatNumber(settlementData?.data?.totalFeeAmount ?? 0)} 원
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 border-[1px] border-[#E9E9E9] bg-white rounded-lg mt-4">
          <h3 className="mb-4 font-semibold text-gray-800">정산 금액</h3>
          <div className="mb-2 space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700">
                  매출액-수수료
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-800">
                  {formatNumber(settlementData?.data?.netSettlementAmount ?? 0)}{" "}
                  원
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between pb-2">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700">
                  정산 예정 금액
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-800">
                  {formatNumber(settlementData?.data?.netSettlementAmount ?? 0)}{" "}
                  원
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center pb-2">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700">
                  정산 완료 금액
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-800">
                  {formatNumber(
                    settlementData?.data?.settlementCompleteAmount ?? 0
                  )}{" "}
                  원
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 정산 정보 */}
        <div className="p-4 border-[1px] border-[#E9E9E9] bg-white rounded-lg mt-4">
          <h3 className="mb-4 font-semibold text-gray-800">정산 정보</h3>
          <div className="mb-2 space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700">
                  사업자 명
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-800">
                  {settlementData?.data?.companyName || "-"}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center pb-2">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700">
                  사업자등록번호
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-800">
                  {settlementData?.data?.businessNumber || "-"}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700">
                  정산 계좌
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-800">
                  {settlementData?.data?.bank || "-"}{" "}
                  {settlementData?.data?.accountNo || "-"}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center pb-2">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700">
                  예금주
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-800">
                  {settlementData?.data?.depositor || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Amount;
